import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GenericDataState } from "type";
import NotificationService from "modules/notifications/api/NotificationService";
import { ALERT_TYPES, AUTOVIEWED_TYPES, NOTIFICATION_SUBJECTS } from "utils/constants";
import { Notification } from "types/notification";
import AuthService from "services/AuthService";
import { DataState } from ".";

const mapNotifications = (response: Notification[]): Notification[] => {
  if (!response) {
    return response;
  }
  const parsedData = response
    .filter((item: Notification) => !ALERT_TYPES.includes(item.type))
    .map((item: Notification) => ({
      ...item,
      creationDateTime: (new Date(item.creationDateTime)).toISOString(),
      subject: NOTIFICATION_SUBJECTS[item.type],
    }));

  return parsedData;
};

const mapAlerts = (response: Notification[]): Notification[] => {
  if (!response) {
    return response;
  }
  const parsedData = response
    .filter((item) => ALERT_TYPES.includes(item.type))
    .map((item) => ({
      ...item,
      creationDateTime: new Date(item.creationDateTime),
      subject: NOTIFICATION_SUBJECTS[item.type],
    }));

  return parsedData;
};

type NotificationsDataState = GenericDataState<Notification[]> & {
  allAlerts: Notification[];
  notificationsView: string;
  openedNotification: Notification | null;
  pendingNotification: Notification | null;
  openedAlert: Notification | null;
  slideOut: boolean;
  theme: string;
  alertsView?: any;
};

const initialState: NotificationsDataState = {
  dataState: DataState.INITIAL,
  data: [],
  allAlerts: [],
  notificationsView: "",
  openedNotification: null,
  pendingNotification: null,
  openedAlert: null,
  slideOut: false,
  theme: AuthService.getTheme(),
};

export const getAllInboxNotificationsList = createAsyncThunk(
  "notifications/allNotifications",
  async (
    params: any,
    thunkApi,
  ) => {
    try {
      const result = await NotificationService.getInboxNotificationsList();

      return result.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const markAsViewed = createAsyncThunk(
  "notifications/markAsViewed",
  async (
    notificationId: string,
    thunkApi,
  ) => {
    try {
      await NotificationService.markAsViewed(notificationId);

      return notificationId;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const openedNotificationSet = createAsyncThunk(
  "notifications/openedNotification",
  async (
    notify: Notification,
    thunkApi,
  ) => {
    try {
      if (notify && !notify.isViewed && AUTOVIEWED_TYPES.includes(notify.type)) {
        await NotificationService.markAsViewed(notify.id);
      }

      return notify;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const notificationsSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    notificationsViewSet: (state, action: { payload: string }) => {
      state.notificationsView = action.payload;
    },
    alertsViewSet: (state, action) => {
      state.alertsView = action.payload;
    },
    // openedNotificationSet: (state, action) => {
    //   state.openedNotification = action.payload;
    // },
    pendingNotificationSet: (state, action) => {
      state.pendingNotification = action.payload;
    },
    openedAlertSet: (state, action) => {
      state.openedAlert = action.payload;
    },
    slideOutSet: (state, action) => {
      state.slideOut = action.payload;
    },
    globalThemeSet: (state, action) => {
      state.theme = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllInboxNotificationsList.pending, (state) => {
      state.dataState = DataState.LOADING;
      state.data = [];
    })
      .addCase(getAllInboxNotificationsList.fulfilled, (state, action) => {
        state.dataState = DataState.FULFILLED;
        state.data = mapNotifications(action.payload);
        state.allAlerts = mapAlerts(action.payload);
      })
      .addCase(getAllInboxNotificationsList.rejected, (state) => {
        state.dataState = DataState.ERROR;
      })
      .addCase(openedNotificationSet.fulfilled, (state, action) => {
        state.openedNotification = action.payload;
        if (action.payload && !action.payload.isViewed && AUTOVIEWED_TYPES.includes(action.payload.type)) {
          state.data = state.data?.map((t) => {
            if (t.id === action.payload.id) {
              return { ...t, isViewed: true };
            }

            return t;
          }) || [];
        }
      })
      .addCase(markAsViewed.fulfilled, (state, action) => {
        state.data = state.data?.map((t) => {
          if (t.id === action.payload) {
            return { ...t, isViewed: true };
          }

          return t;
        }) || [];
      });
  },
});

export default notificationsSlice.reducer;

export const selectAllNotifications = (state: { notification: NotificationsDataState }) => state.notification?.data ?? [];
export const selectAllNotificationsState = (state: { notification: NotificationsDataState }) => state.notification.dataState;
export const selectAllAlerts = (state: { notification: NotificationsDataState }) => state.notification?.allAlerts ?? [];
export const selectNewNotifications = (state: { notification: NotificationsDataState }) => {
  const notifications = state.notification.data?.filter((item: Notification) => !item.isViewed);

  return notifications ?? [];
};
export const selectNewAlerts = (state: { notification: NotificationsDataState }) => state.notification.allAlerts.filter((item: Notification) => !item.isViewed);
export const selectNotificationsView = (state: { notification: NotificationsDataState }) => state.notification.notificationsView;
export const selectAlertsView = (state: { notification: NotificationsDataState }) => state.notification.alertsView;
export const selectOpenedNotification = (state: { notification: NotificationsDataState }) => state.notification.openedNotification;
export const selectOpenedAlert = (state: { notification: NotificationsDataState }) => state.notification.openedAlert;
export const selectPendingNotification = (state: { notification: NotificationsDataState }) => state.notification.pendingNotification;
export const selectSlideOut = (state: { notification: NotificationsDataState }) => state.notification.slideOut;
export const selectTheme = (state: { notification: NotificationsDataState }) => state.notification.theme;
export const selectIsNewNotifications = (state: { notification: NotificationsDataState }) => state.notification.data?.some((item: Notification) => !item.isViewed);
export const selectIsNewAlerts = (state: { notification: NotificationsDataState }) => state.notification.allAlerts?.some((item: Notification) => !item.isViewed);

export const { alertsViewSet, notificationsViewSet, pendingNotificationSet, slideOutSet, globalThemeSet, openedAlertSet } = notificationsSlice.actions;
